// src/components/MinPriceEditor.jsx — WAGON R5 lot F (prix plancher bloquant)
//
// Bloc éditeur du prix plancher d'un logement (carte « Tarification générale »
// de la fiche logement). Re-sourcé V2 : lit/écrit
// logement_pricing_config.min_price via useMinPrice (POST pricing_set_min_price).
//
//   - Champ « Prix plancher (minimum, €) » pré-rempli avec min_price.
//   - Bouton « Enregistrer » → save(value) ; vide → clear (NULL = aucun blocage).
//
// La valeur sert de borne basse BLOQUANTE à la saisie manuelle calendrier
// (handlePricingSetManual, 400 below_floor + override confirmé). Pas d'action
// « appliquer » : le plancher n'est jamais poussé à Beds24.

import { useState, useEffect } from "react";
import { DS } from "../lib/ui";
import { useMinPrice } from "../lib/use-min-price";

export default function MinPriceEditor({ logementId }) {
  const { minPrice, loading, error, save } = useMinPrice(logementId);

  const [inputValue, setInputValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { kind, msg }

  // Sync input ↔ valeur DB post-fetch.
  useEffect(() => {
    if (loading) return;
    setInputValue(minPrice == null ? "" : String(minPrice));
  }, [minPrice, loading]);

  function flashToast(kind, msg, ms = 4000) {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), ms);
  }

  async function handleSave() {
    let parsed = null;
    if (inputValue !== "" && inputValue != null) {
      const n = Number(inputValue);
      if (!Number.isFinite(n) || n <= 0 || n > 9999) {
        flashToast("err", "Doit être un nombre entre 1 et 9999 (ou vide).");
        return;
      }
      parsed = n;
    }
    setSaving(true);
    const result = await save(parsed);
    setSaving(false);
    if (!result.ok) {
      flashToast("err", `Erreur : ${result.error || "inconnue"}`);
      return;
    }
    flashToast(
      "ok",
      parsed == null
        ? "Prix plancher effacé (aucun blocage)."
        : `Prix plancher ${parsed}€ enregistré.`,
      5000,
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: DS.navy }}>
            Prix plancher (minimum, €)
          </span>
          <input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="ex: 70 (vide = aucun blocage)"
            data-testid="min-price-input"
            style={{
              width: 160,
              padding: "7px 10px",
              borderRadius: 7,
              border: "1px solid #cdd7e3",
              fontSize: 14,
              background: "#fff",
              color: "#222",
            }}
          />
        </label>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="min-price-save"
          style={{
            padding: "8px 14px",
            borderRadius: 7,
            border: "none",
            background: DS.navy,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "…" : "Enregistrer"}
        </button>
      </div>

      <div
        style={{
          fontSize: 11,
          color: "#666",
          marginTop: 6,
          padding: "6px 10px",
          background: "#f8fafd",
          borderRadius: 6,
          lineHeight: 1.4,
        }}
      >
        ℹ️ Bloque la saisie manuelle d'un prix inférieur au calendrier
        (confirmation pour passer outre). Vide = aucun blocage.
      </div>

      {error && (
        <div style={{ fontSize: 11, color: "#c0392b", marginTop: 6 }}>
          Erreur chargement : {error}
        </div>
      )}
      {toast && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: toast.kind === "ok" ? "#1d7a46" : "#c0392b",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
